import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { BarChart2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { STAT_FORM_OPTIONS } from '@/lib/statOptions'
import { IS_DEMO_MODE } from '@/config/config'

const { formatEchanges, contenuEchanges, presences, entreeDansAppartement, distributionKitRDR } = STAT_FORM_OPTIONS

interface StatFormProps {
    formData: any
    onSubmit: (data: any) => void
    onBack: () => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const StatForm = ({
    formData,
    onSubmit,
    onBack,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm
}: StatFormProps) => {
    const [statData, setStatData] = useState({
        uuid: formData.statData?.uuid || crypto.randomUUID(),
        locataire: formData.entryData?.locataire || formData.statData?.locataire || '',
        travailleur: formData.entryData?.travailleur || formData.statData?.travailleur || '',
        collegue: formData.entryData?.collegue || formData.statData?.collegue || [],
        date: formData.entryData?.date || formData.statData?.date || new Date().toISOString().split('T')[0],
        duree: formData.statData?.duree || 15,
        format_echange: formData.statData?.format_echange || '',
        contenu_echange: formData.statData?.contenu_echange || '',
        contenu_echange_real: formData.statData?.contenu_echange_real || [],
        presence: formData.statData?.presence || 'Présent.e',
        entree_dans_appartement: formData.statData?.entree_dans_appartement || 'Oui',
        distribution_kit_rdr: formData.statData?.distribution_kit_rdr || 'Non',
        note: formData.statData?.note || '',
        submit_ts: formData.statData?.submit_ts || null
    })

    const [errors, setErrors] = useState<string[]>([])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        // The demo lets visitors go through the flow with empty fields.
        const newErrors: string[] = []
        if (!IS_DEMO_MODE) {
            if (!statData.format_echange) newErrors.push('Format de l\'échange requis')
            if (!statData.contenu_echange) newErrors.push('Contenu de l\'échange requis')
            if (statData.contenu_echange_real.length === 0) newErrors.push('Contenu effectivement abordé requis')
        }

        setErrors(newErrors)

        if (newErrors.length === 0) {
            onSubmit(statData)
        }
    }

    const handleBack = () => {
        onBack()
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Nouvelle stat" icon={<BarChart2 size={20} />} onBack={onNavigateHome} />
            <div className="max-w-4xl mx-auto py-8 px-4">
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
                    {errors.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                            <h3 className="text-red-800 font-medium">Informations manquantes :</h3>
                            <ul className="list-disc list-inside text-red-700">
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nom du.de la locataire
                            </label>
                            <input
                                type="text"
                                value={statData.locataire}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Travailleur.euse
                            </label>
                            <input
                                type="text"
                                value={statData.travailleur}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Collègue.s HF présent.e.s
                            </label>
                            <input
                                type="text"
                                value={Array.isArray(statData.collegue) ? statData.collegue.join(', ') : statData.collegue}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date de l'échange
                            </label>
                            <input
                                type="date"
                                value={statData.date}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Durée de l'échange (min)
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="5"
                                value={statData.duree}
                                onChange={(e) => setStatData({ ...statData, duree: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Format de l'échange
                            </label>
                            <select
                                value={statData.format_echange}
                                onChange={(e) => {
                                    const format = e.target.value
                                    const updates: any = { format_echange: format }
                                    if (format === 'Contact téléphonique') {
                                        updates.presence = 'Non applicable à cet échange'
                                        updates.entree_dans_appartement = 'Non'
                                    }
                                    setStatData({ ...statData, ...updates })
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Sélectionner...</option>
                                {formatEchanges.map(format => (
                                    <option key={format} value={format}>{format}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contenu initialement prévu de l'échange
                            </label>
                            <select
                                value={statData.contenu_echange}
                                onChange={(e) => setStatData({ ...statData, contenu_echange: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Sélectionner...</option>
                                {contenuEchanges.map(contenu => (
                                    <option key={contenu} value={contenu}>{contenu}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contenu effectivement abordé
                            </label>
                            <div className="border border-gray-300 rounded-md px-3 py-2 space-y-1">
                                {contenuEchanges.map(contenu => (
                                    <label key={contenu} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={statData.contenu_echange_real.includes(contenu)}
                                            onChange={(e) => {
                                                const updated = e.target.checked
                                                    ? [...statData.contenu_echange_real, contenu]
                                                    : statData.contenu_echange_real.filter((v: string) => v !== contenu)
                                                setStatData({ ...statData, contenu_echange_real: updated })
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm text-gray-700">{contenu}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Distribution de kit RDR
                        </label>
                        <select
                            value={statData.distribution_kit_rdr}
                            onChange={(e) => setStatData({ ...statData, distribution_kit_rdr: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {distributionKitRDR.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Présence physique du.de la locataire
                            </label>
                            <div className="space-y-2">
                                {presences.map(presence => (
                                    <label key={presence} className="flex items-center">
                                        <input
                                            type="radio"
                                            name="presence"
                                            value={presence}
                                            checked={statData.presence === presence}
                                            onChange={(e) => setStatData({ ...statData, presence: e.target.value })}
                                            className="mr-2"
                                        />
                                        {presence}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Entrée dans l'appartement
                            </label>
                            <div className="space-y-2">
                                {entreeDansAppartement.map(entree => (
                                    <label key={entree} className="flex items-center">
                                        <input
                                            type="radio"
                                            name="entree_dans_appartement"
                                            value={entree}
                                            checked={statData.entree_dans_appartement === entree}
                                            onChange={(e) => setStatData({ ...statData, entree_dans_appartement: e.target.value })}
                                            className="mr-2"
                                        />
                                        {entree}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={handleBack}>
                            Retour
                        </Button>
                        <div className="space-x-4">
                            <Button type="button" variant="outline" onClick={onNavigateHome}>
                                Accueil
                            </Button>
                            <Button type="submit">
                                Suivant
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
} 